import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button } from '@artosera/ui';
import {
  lireCibles,
  lireProfil,
  listerProfils,
  listerVersionsProfil,
  NATURES_PIVOT,
  type NaturePivot,
} from '../lib/correspondance.js';
import { formaterDate } from '../lib/format.js';
import pageStyles from '../styles/page.module.css';
import tableStyles from '../styles/table.module.css';
import { messageReprise } from './tabs/reprise/messages.js';
import { cleProfils } from './tabs/reprise/correspondance/EditeurCorrespondance.js';
import { ReglesLecture } from './tabs/reprise/correspondance/ReglesLecture.js';
import styles from './tabs/reprise/Reprise.module.css';
import corr from './tabs/reprise/correspondance/Correspondance.module.css';

/**
 * La bibliothèque de profils de correspondance (`/reprise/profils`) : un
 * profil par logiciel d'origine et par nature, réutilisable d'une galerie à
 * l'autre. On y consulte la liste, les versions et les règles ; on n'y
 * modifie rien. Une version est immuable : l'édition se fait depuis un lot,
 * et publie la version suivante.
 */
export function ProfilsBibliotheque(): JSX.Element {
  const { t } = useTranslation('console');
  const [parametres, setParametres] = useSearchParams();
  const nature = NATURES_PIVOT.find((valeur) => valeur === parametres.get('nature')) ?? null;
  const profilId = parametres.get('profil');
  const versionDemandee = Number(parametres.get('version')) || undefined;

  const modifier = (suite: Record<string, string | null>): void => {
    const prochains = new URLSearchParams(parametres);
    for (const [cle, valeur] of Object.entries(suite)) {
      if (valeur === null) prochains.delete(cle);
      else prochains.set(cle, valeur);
    }
    setParametres(prochains);
  };

  const liste = useQuery({
    queryKey: [...cleProfils, 'liste', nature ?? ''],
    queryFn: () => listerProfils(nature ?? undefined),
  });
  const versions = useQuery({
    queryKey: [...cleProfils, profilId ?? '', 'versions'],
    queryFn: () => listerVersionsProfil(profilId!),
    enabled: profilId !== null,
  });
  const version = versionDemandee ?? versions.data?.[0]?.version;
  const profil = useQuery({
    queryKey: [...cleProfils, profilId ?? '', version ?? 0],
    queryFn: () => lireProfil(profilId!, version),
    enabled: profilId !== null && version !== undefined,
  });
  const cibles = useQuery({ queryKey: [...cleProfils, 'cibles'], queryFn: lireCibles });

  return (
    <>
      <header className={pageStyles.entete}>
        <div>
          <h1>{t('correspondance.bibliotheque.titre')}</h1>
          <p>{t('correspondance.bibliotheque.texte')}</p>
        </div>
      </header>

      <section className={pageStyles.panneau} aria-labelledby="profils-liste">
        <div className={pageStyles.sectionTitre}>
          <h2 id="profils-liste">{t('correspondance.bibliotheque.liste')}</h2>
          <div className={corr.parametre}>
            <label htmlFor="profils-nature" className={corr.sansTexte}>
              {t('correspondance.bibliotheque.filtreNature')}
            </label>
            <select
              id="profils-nature"
              className={corr.selection}
              value={nature ?? ''}
              onChange={(e) =>
                modifier({ nature: e.target.value || null, profil: null, version: null })
              }
            >
              <option value="">{t('correspondance.bibliotheque.toutesNatures')}</option>
              {NATURES_PIVOT.map((valeur: NaturePivot) => (
                <option key={valeur} value={valeur}>
                  {t(`reprise.natures.${valeur}`)}
                </option>
              ))}
            </select>
          </div>
        </div>
        {liste.isLoading ? <p className={pageStyles.videEtat}>{t('reprise.chargement')}</p> : null}
        {liste.isError ? (
          <p className={styles.erreur} role="alert">
            {messageReprise(t, liste.error) ?? t('correspondance.bibliotheque.erreur')}
          </p>
        ) : null}
        {liste.isSuccess && liste.data.length === 0 ? (
          <p className={pageStyles.videEtat}>{t('correspondance.bibliotheque.aucun')}</p>
        ) : null}
        {liste.isSuccess && liste.data.length > 0 ? (
          <table className={`${tableStyles.table} ${styles.table}`}>
            <thead>
              <tr>
                <th scope="col" className={styles.colProfilNom}>
                  {t('correspondance.bibliotheque.colonnes.nom')}
                </th>
                <th scope="col" className={styles.colProfilLogiciel}>
                  {t('correspondance.bibliotheque.colonnes.application')}
                </th>
                <th scope="col" className={styles.colProfilNature}>
                  {t('correspondance.bibliotheque.colonnes.nature')}
                </th>
                <th scope="col" className={styles.colProfilVersion}>
                  {t('correspondance.bibliotheque.colonnes.version')}
                </th>
                <th scope="col" className={styles.colonneEtroite}>
                  <span className={corr.sansTexte}>
                    {t('correspondance.bibliotheque.colonnes.action')}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {liste.data.map((ligne) => (
                <tr key={ligne.id} aria-current={ligne.id === profilId ? 'true' : undefined}>
                  <td data-label={t('correspondance.bibliotheque.colonnes.nom')}>
                    <span className={tableStyles.cellulePrincipale}>{ligne.nom}</span>
                    {ligne.fourni ? (
                      <>
                        {' '}
                        <Badge tone="neutral">{t('correspondance.bibliotheque.fourni')}</Badge>
                      </>
                    ) : null}
                  </td>
                  <td data-label={t('correspondance.bibliotheque.colonnes.application')}>
                    {ligne.application || '—'}
                  </td>
                  <td data-label={t('correspondance.bibliotheque.colonnes.nature')}>
                    {t(`reprise.natures.${ligne.nature}`)}
                  </td>
                  <td data-label={t('correspondance.bibliotheque.colonnes.version')}>
                    {ligne.version}
                  </td>
                  <td data-label={t('correspondance.bibliotheque.colonnes.action')}>
                    <Button
                      variant={ligne.id === profilId ? 'secondary' : 'ghost'}
                      size="sm"
                      aria-label={t('correspondance.bibliotheque.consulter', { nom: ligne.nom })}
                      onClick={() => modifier({ profil: ligne.id, version: null })}
                    >
                      {t('correspondance.bibliotheque.consulterCourt')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </section>

      {profilId !== null ? (
        <section className={pageStyles.panneau} aria-labelledby="profils-detail">
          <div className={pageStyles.sectionTitre}>
            <h2 id="profils-detail">
              {profil.data
                ? t('correspondance.bibliotheque.detail', {
                    nom: profil.data.nom,
                    version: profil.data.version,
                  })
                : t('correspondance.bibliotheque.detailChargement')}
            </h2>
          </div>
          <div className={styles.pile}>
            <p className={styles.texte}>{t('correspondance.bibliotheque.lectureSeule')}</p>
            {versions.isSuccess ? (
              <div className={styles.pile}>
                <h3 className={styles.sousTitre}>{t('correspondance.bibliotheque.versions')}</h3>
                <ul className={corr.versions}>
                  {versions.data.map((ligne) => (
                    <li key={ligne.version}>
                      <Button
                        variant={ligne.version === version ? 'secondary' : 'ghost'}
                        size="sm"
                        aria-pressed={ligne.version === version}
                        onClick={() => modifier({ version: String(ligne.version) })}
                      >
                        {t('correspondance.bibliotheque.versionNumero', { version: ligne.version })}
                      </Button>
                      <span className={styles.discret}>
                        {[
                          formaterDate(ligne.creeLe, '—'),
                          ligne.auteur ?? t('correspondance.bibliotheque.fournie'),
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {profil.isError || versions.isError ? (
              <p className={styles.erreur} role="alert">
                {messageReprise(t, profil.error ?? versions.error) ??
                  t('correspondance.bibliotheque.erreur')}
              </p>
            ) : null}
            {profil.data ? (
              <>
                <p className={styles.discret}>
                  {t('correspondance.bibliotheque.signature', {
                    count: profil.data.signature.length,
                  })}
                </p>
                <ReglesLecture regles={profil.data.regles} cibles={cibles.data} />
              </>
            ) : null}
            <div className={styles.actions}>
              <Button variant="ghost" onClick={() => modifier({ profil: null, version: null })}>
                {t('correspondance.bibliotheque.fermer')}
              </Button>
            </div>
          </div>
        </section>
      ) : null}
    </>
  );
}
